import { useEffect, useState, useMemo } from "react";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import Pagination from "../../../../components/Pagination";
import api from "../../../../api/api";
import * as bootstrap from "bootstrap";
import Breadcrumbs from "../../../../components/Breadcrumbs";
import Table from "../../../../components/table";
import usePagination from "../../../../utils/usePagination";
import "../../Common/form.css";

const VendorMaster = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [selectedVendor, setSelectedVendor] = useState(null);
  const [modalType, setModalType] = useState(null);

  const filteredData = useMemo(() => {
    return vendors.filter(
      (vendor) =>
        vendor.name?.toLowerCase().includes(search.toLowerCase()) ||
        vendor.location?.toLowerCase().includes(search.toLowerCase()) ||
        vendor.pan_number?.toLowerCase().includes(search.toLowerCase()) ||
        vendor.gst_number?.toLowerCase().includes(search.toLowerCase()),
    );
  }, [vendors, search]);

  const {
    currentPage,
    itemsPerPage,
    totalPages,
    totalItems,
    currentItems,
    goToPage,
    handleItemsPerPageChange,
  } = usePagination(filteredData, 10);

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const response = await api.get("/master-data/vendor-master");
      setVendors(response.data.vendors || []);
    } catch (err) {
      setError("Failed to fetch vendors");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVendors();
  }, []);

  useEffect(() => {
    const tooltipTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="tooltip"]'),
    );
    const tooltips = tooltipTriggerList.map((el) => new bootstrap.Tooltip(el));
    return () => tooltips.forEach((t) => t.dispose());
  }, [currentItems]);

  const handleOpenModal = (vendor, type) => {
    setSelectedVendor(vendor);
    setModalType(type);
  };

  const handleCloseModal = () => {
    setSelectedVendor(null);
    setModalType(null);
  };

  const handleSaveChanges = async () => {
    try {
      await api.put(
        `/master-data/vendor-master/${selectedVendor.vendor_id}`,
        {
          name: selectedVendor.name,
          location: selectedVendor.location,
          pan_number: selectedVendor.pan_number,
          gst_number: selectedVendor.gst_number,
          tds_percentage: selectedVendor.tds_percentage,
        },
      );

      setVendors((prev) =>
        prev.map((v) =>
          v.vendor_id === selectedVendor.vendor_id ? selectedVendor : v,
        ),
      );

      handleCloseModal();
    } catch (err) {
      console.error("Error updating vendor:", err);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await api.delete(`/master-data/vendor-master/${selectedVendor.vendor_id}`);
      setVendors((prev) =>
        prev.filter((v) => v.vendor_id !== selectedVendor.vendor_id)
      );
      handleCloseModal();
    } catch (err) {
      console.error("Failed to delete vendor", err);
    }
  };

  const columns = [
    { label: "Name", accessor: "name" },
    { label: "Location", accessor: "location" },
    { label: "PAN Number", accessor: "pan_number" },
    { label: "GST Number", accessor: "gst_number" },
    { label: "TDS(%)", accessor: "tds_percentage" },
    {
      label: "Action",
      render: (row) => (
        <>
          <EditIcon
            onClick={() => handleOpenModal(row, "edit")}
            style={{ cursor: "pointer", color: "#1e305e", marginRight: "10px" }}
          />
          <DeleteIcon
            onClick={() => handleOpenModal(row, "delete")}
            style={{ cursor: "pointer", color: "#1e305e" }}
          />
        </>
      ),
    },
  ];

  return (
    <>
      <Breadcrumbs />
      <div className="mb-3 d-flex align-items-center position-relative">
        <input
          type="text"
          className="form-control"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            goToPage(1);
          }}
        />
        {search && (
          <CancelIcon
            className="position-absolute"
            onClick={() => setSearch("")}
            style={{ right: "15px", cursor: "pointer", color: "#6c757d" }}
          />
        )}
      </div>
      <Table
        columns={columns}
        data={currentItems}
        loading={loading}
        emptyMessage="No vendors found."
        itemsPerPage={itemsPerPage}
        pagination={
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={goToPage}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        }
      />

      {selectedVendor && (
        <div
          className="modal fade show"
          style={{ display: "block", background: "rgba(0,0,0,0.5)" }}
          tabIndex="-1"
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {modalType === "edit" ? "Edit Vendor" : "Confirm Delete"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                ></button>
              </div>

              <div className="modal-body">
                {modalType === "edit" ? (
                  <form>
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedVendor.name || ""}
                        onChange={(e) =>
                          setSelectedVendor({
                            ...selectedVendor,
                            name: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Location</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedVendor.location || ""}
                        onChange={(e) =>
                          setSelectedVendor({
                            ...selectedVendor,
                            location: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">PAN Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedVendor.pan_number || ""}
                        onChange={(e) =>
                          setSelectedVendor({
                            ...selectedVendor,
                            pan_number: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">GST Number</label>
                      <input
                        type="text"
                        className="form-control"
                        value={selectedVendor.gst_number || ""}
                        onChange={(e) =>
                          setSelectedVendor({
                            ...selectedVendor,
                            gst_number: e.target.value,
                          })
                        }
                      />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">TDS(%)</label>
                      <input
                        type="number"
                        className="form-control"
                        value={selectedVendor.tds_percentage || ""}
                        onChange={(e) =>
                          setSelectedVendor({
                            ...selectedVendor,
                            tds_percentage: e.target.value,
                          })
                        }
                      />
                    </div>
                  </form>
                ) : (
                  <div className="text-center">
                    <p>Are you sure you want to delete this vendor?</p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={handleCloseModal}>
                  Cancel
                </button>
                {modalType === "delete" ? (
                  <button className="btn btn-danger" onClick={handleConfirmDelete}>
                    Confirm Delete
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={handleSaveChanges}>
                    Save Changes
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default VendorMaster;